import React, { useRef, useState } from 'react';

import Check from '@mui/icons-material/Check';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import ExcludeFromReports from 'assets/icons/settings-menu/ExcludeFromReports.inline.svg';
import ExcludeFromSpendingPlan from 'assets/icons/settings-menu/ExcludeFromSpendingPlan.inline.svg';

import TransferExclusion from 'components/Transactions/VisibilitySection/transferExclusion';
import ExclusionOptionContent from 'components/Transactions/ExclusionOptionContent';

import { useExclusionsButtonStyles } from './styles';

export type ExclusionKey = 'spendingPlan' | 'reports';

export type TransferExclusionState = {
  excludeF2S?: boolean;
  excludeF2SDest?: boolean;
  excludeReports?: boolean;
  excludeReportsDest?: boolean;
  /** Money Out (source) side — the checkbox reports its target checked state directly. */
  onChangeF2S: (value: boolean) => void;
  /** Money In (destination) side — the checkbox has no value of its own, so this flips the current one. */
  onToggleF2SDest: () => void;
  onChangeReports: (value: boolean) => void;
  onToggleReportsDest: () => void;
};

type ExclusionsButtonProps = {
  spendingPlan: boolean;
  reports: boolean;
  onToggle: (key: ExclusionKey) => void;
  /** Transfers exclude per money-direction rather than a single flag — swaps the checkbox menu for the shared transfer grid. */
  transfer?: TransferExclusionState;
  /** Opening balance / balance adjustment categories force Reports exclusion permanently — locks the Reports row on. */
  disabledReports?: boolean;
};

const ROWS: ReadonlyArray<{
  key: ExclusionKey;
  label: string;
  helperText: string;
  Icon: typeof ExcludeFromReports;
}> = [
  {
    key: 'spendingPlan',
    label: 'Spending Plan',
    helperText: 'Affects Spending Plan calculations.',
    Icon: ExcludeFromSpendingPlan,
  },
  {
    key: 'reports',
    label: 'Reports',
    helperText: 'Affects Reports, Watchlists, and Upcoming Summary.',
    Icon: ExcludeFromReports,
  },
];

/**
 * A transfer excludes per money-direction rather than as one flag, so the
 * "Spending Plan & Reports" bucket used for the plain source-only flow can't
 * summarize it — show the "Money out | Money in" split face whenever the two
 * directions actually differ on at least one destination.
 */
export const shouldShowMoneyDirectionSplit = (transfer: TransferExclusionState | undefined): boolean =>
  Boolean(transfer) &&
  (Boolean(transfer?.excludeF2S) !== Boolean(transfer?.excludeF2SDest) ||
    Boolean(transfer?.excludeReports) !== Boolean(transfer?.excludeReportsDest));

type ExclusionsButtonState = 'none' | 'spendingPlan' | 'reports' | 'both';

const BUTTON_CONTENT: Record<ExclusionsButtonState, { icon: React.ReactNode; text: string }> = {
  none: { icon: <VisibilityRoundedIcon />, text: 'Exclusions' },
  spendingPlan: { icon: <ExcludeFromSpendingPlan width={20} height={20} />, text: 'Spending Plan' },
  reports: { icon: <ExcludeFromReports width={20} height={20} />, text: 'Reports' },
  both: {
    icon: null,
    text: 'Spending Plan & Reports',
  },
};

/**
 * Exclusions entry on the Series details pane: an outlined button whose icon
 * and label track which destinations are currently excluded. Opens an
 * anchored checkbox menu (the same pattern as the "Always include" menu in
 * SplitDialog) rather than a separate dialog, so each toggle applies
 * immediately and the menu stays open for picking more than one destination.
 * Shared between the Bill Setup flow and the Create/Edit Series dialog.
 */
export default function ExclusionsButton({
  spendingPlan,
  reports: reportsProp,
  onToggle,
  transfer,
  disabledReports,
}: ExclusionsButtonProps): React.JSX.Element {
  const reports = disabledReports || reportsProp;
  // Anchored to this wrapper (not the clicked element) because the button
  // underneath swaps component type (Button <-> ButtonBase) as the transfer
  // exclusion flags change while the menu is open — anchoring to
  // event.currentTarget would point at a node that gets unmounted mid-toggle,
  // and MUI falls back to positioning the menu at the viewport origin once
  // its anchor is detached.
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const selected: Record<ExclusionKey, boolean> = { spendingPlan, reports };
  const both = spendingPlan && reports;

  const openMenu = () => setMenuOpen(true);
  const closeMenu = () => setMenuOpen(false);

  const buttonState: ExclusionsButtonState = both
    ? 'both'
    : spendingPlan
      ? 'spendingPlan'
      : reports
        ? 'reports'
        : 'none';
  const { icon, text } = BUTTON_CONTENT[buttonState];
  const { classes, cx } = useExclusionsButtonStyles({ active: buttonState !== 'none' });
  const buttonIcon =
    buttonState === 'both' ? (
      <Box className={classes.bothIcon}>
        <ExcludeFromSpendingPlan width={20} height={20} />
        <ExcludeFromReports width={20} height={20} />
      </Box>
    ) : (
      icon
    );

  const outSpendingPlan = Boolean(transfer?.excludeF2S);
  const outReports = Boolean(transfer?.excludeReports);
  const inSpendingPlan = Boolean(transfer?.excludeF2SDest);
  const inReports = Boolean(transfer?.excludeReportsDest);
  const showMoneyDirectionSplit = shouldShowMoneyDirectionSplit(transfer);

  return (
    <>
      <Box ref={anchorRef} className={classes.anchor}>
        {showMoneyDirectionSplit ? (
          <Box className={classes.moneyDirectionSplitWrapper}>
            <ButtonBase
              id='exclusions-button-out'
              onClick={openMenu}
              className={cx(
                classes.moneyDirectionButton,
                classes.moneyDirectionButtonOut,
                (outSpendingPlan || outReports) && classes.moneyDirectionButtonSelected,
              )}
            >
              <Typography variant='captionMedium'>Money out</Typography>
              {outSpendingPlan && <ExcludeFromSpendingPlan width={20} height={20} />}
              {outReports && <ExcludeFromReports width={20} height={20} />}
            </ButtonBase>
            <ButtonBase
              id='exclusions-button-in'
              onClick={openMenu}
              className={cx(
                classes.moneyDirectionButton,
                (inSpendingPlan || inReports) && classes.moneyDirectionButtonSelected,
              )}
            >
              <Typography variant='captionMedium'>Money in</Typography>
              {inSpendingPlan && <ExcludeFromSpendingPlan width={20} height={20} />}
              {inReports && <ExcludeFromReports width={20} height={20} />}
            </ButtonBase>
          </Box>
        ) : (
          <Button
            id='exclusions-button'
            onClick={openMenu}
            className={classes.fullWidthSplitButton}
            variant='outlined'
            startIcon={buttonIcon}
          >
            <Typography variant='paragraphMedium'>{text}</Typography>
          </Button>
        )}
      </Box>

      <Menu anchorEl={anchorRef.current} open={menuOpen} onClose={closeMenu}>
        {transfer ? (
          <TransferExclusion
            excludeF2S={transfer.excludeF2S}
            excludeF2SDest={transfer.excludeF2SDest}
            setExcludeF2S={transfer.onChangeF2S}
            setExcludeF2SDest={transfer.onToggleF2SDest}
            excludeReports={transfer.excludeReports}
            excludeReportsDest={transfer.excludeReportsDest}
            setExcludeReports={transfer.onChangeReports}
            setExcludeReportsDest={transfer.onToggleReportsDest}
            disabledReports={disabledReports}
          />
        ) : (
          // MUI's Menu clones each child directly (for focus/keyboard-nav
          // wiring) and doesn't support a Fragment as a single child — an
          // array of the same items works fine.
          [
            <Typography
              key='exclusions-menu-label'
              variant='paragraphRegular'
              color='text.secondary'
              id='exclusions-menu-label'
              className={classes.menuLabel}
            >
              Exclude from:
            </Typography>,
            ...ROWS.map(({ key, label, helperText, Icon }) => {
              const isSelected = selected[key];
              const isLockedReports = key === 'reports' && disabledReports;
              return (
                <MenuItem
                  key={key}
                  id={`series-exclusions-${key}`}
                  role='menuitemcheckbox'
                  aria-checked={isSelected}
                  aria-describedby='exclusions-menu-label'
                  disabled={isLockedReports}
                  onClick={() => onToggle(key)}
                >
                  <ListItemIcon className={cx(classes.menuItemIcon, isSelected && classes.menuItemIconSelected)}>
                    {isSelected && <Check fontSize='small' />}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <ExclusionOptionContent label={label} helperText={helperText} Icon={Icon} selected={isSelected} />
                    }
                  />
                </MenuItem>
              );
            }),
          ]
        )}
      </Menu>
    </>
  );
}
